import * as React from "react";

interface VideoModalProps {
  onClose: () => void;
  videoUrl: string;
  triggerRef?: React.RefObject<HTMLElement>;
}

function getYoutubeVideoId(url: string): string {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = regExp.exec(url);
  return match && match[2].length === 11 ? match[2] : "";
}

export function VideoModal({ onClose, videoUrl, triggerRef }: Readonly<VideoModalProps>): JSX.Element | null {
  const dialogRef = React.useRef<HTMLDialogElement>(null);

  const videoId = getYoutubeVideoId(videoUrl);

  // Handle escape key to close modal
  React.useEffect(() => {
    const dialogNode = dialogRef.current;
    if (!dialogNode) {
      return;
    }

    const handleCancel = (_event: Event) => {
      onClose();
    };

    const handleClick = (event: MouseEvent) => {
      if (event.target === dialogNode) {
        onClose();
      }
    };

    dialogNode.addEventListener("cancel", handleCancel);
    dialogNode.addEventListener("click", handleClick);

    // Store original body styles
    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    const originalHeight = document.body.style.height;
    const originalPosition = document.body.style.position;

    // Calculate scrollbar width to prevent layout shift
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;

    // Disable scrolling while modal is open - multiple approaches for better cross-browser support
    document.body.style.overflow = "hidden";
    document.body.style.paddingRight = `${scrollBarWidth}px`;
    document.body.style.height = "100%";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";

    // Open the dialog when component mounts
    if (dialogRef.current && !dialogRef.current.open) {
      dialogRef.current.showModal();
    }

    // Apply backdrop style
    const styleElement = document.createElement("style");
    styleElement.textContent = `
      dialog::backdrop {
        background-color: rgba(0, 0, 0, 0.7);
        backdrop-filter: blur(2px);
        width: 100%;
        height: 100%;
      }
      
      html.dialog-open, body.dialog-open {
        overflow: hidden;
        position: fixed;
        width: 100%;
      }
    `;
    document.head.appendChild(styleElement);

    // Add class to html and body for additional CSS control
    document.documentElement.classList.add("dialog-open");
    document.body.classList.add("dialog-open");

    // Prevent touch scrolling on mobile
    function preventTouchMove(e: TouchEvent) {
      if (!dialogRef.current?.contains(e.target as Node)) {
        e.preventDefault();
      }
    }

    document.addEventListener("touchmove", preventTouchMove, { passive: false });

    return () => {
      dialogNode.removeEventListener("cancel", handleCancel);
      dialogNode.removeEventListener("click", handleClick);
      document.removeEventListener("touchmove", preventTouchMove);

      // Restore original body styles
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
      document.body.style.height = originalHeight;
      document.body.style.position = originalPosition;
      document.body.style.width = "";

      // Remove classes
      document.documentElement.classList.remove("dialog-open");
      document.body.classList.remove("dialog-open");

      // Remove style element
      document.head.removeChild(styleElement);

      // Return focus to the trigger element when dialog closes
      if (triggerRef?.current) {
        triggerRef.current.focus();
      }
    };
  }, [onClose, triggerRef]);

  if (!videoId) {
    console.error("Invalid YouTube URL");
    return null;
  }

  return (
    <dialog
      ref={dialogRef}
      className="tw-m-auto tw-flex tw-w-1/2 tw-animate-fadeIn tw-flex-col tw-justify-center tw-overflow-visible tw-border-none tw-bg-black tw-p-0 max-xl:tw-w-[95%] max-lg:tw-h-[95%]"
    >
      <div className="tw-flex tw-items-center tw-justify-end tw-px-4 tw-py-3">
        <button
          onClick={onClose}
          className="tw-bg-gray-700 hover:tw-bg-gray-600 tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-text-white focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-brand_scale-500 focus-visible:tw-ring-opacity-75"
          aria-label="Close"
        >
          <i className="fa fa-close" />
        </button>
      </div>
      <div className="lightBorder tw-aspect-video tw-w-full tw-overflow-hidden tw-rounded-lg tw-border">
        <iframe
          width="100%"
          height="100%"
          src={`https://www.youtube.com/embed/${videoId}?rel=0`}
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="tw-aspect-video"
          autoFocus
        ></iframe>
      </div>
    </dialog>
  );
}
